import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import './ComparePages.css';

const ChatGPTCompare = () => {
  const navigate = useNavigate();

  const comparisonData = [
    { 
      category: '대상 사용자', 
      chatgpt: '일반 사용자', 
      whatquiz: '교사 전용 (인증 필수)',
      highlight: true
    },
    { 
      category: '생기부 작성', 
      chatgpt: '직접 프롬프트 작성 필요', 
      whatquiz: '전용 양식으로 3분 완성',
      highlight: true
    },
    { 
      category: '2026 기재요령', 
      chatgpt: '반영 안됨', 
      whatquiz: '완벽 반영 (자동 검증)',
      highlight: true
    },
    { 
      category: '학습지 생성', 
      chatgpt: '수동 요청', 
      whatquiz: '과목/난이도 선택만으로 완성',
      highlight: false
    },
    { 
      category: '수행평가 설계', 
      chatgpt: '지원 안함', 
      whatquiz: '채점기준표 자동 생성',
      highlight: true
    },
    { 
      category: 'AI 모델', 
      chatgpt: 'GPT 단일 모델', 
      whatquiz: 'GPT + Claude + Gemini',
      highlight: true
    },
    { 
      category: '학교 예산 결제', 
      chatgpt: '해외 결제만 가능', 
      whatquiz: '법인카드, 세금계산서 발급',
      highlight: true
    },
    { 
      category: '가격', 
      chatgpt: '월 $20 (약 27,000원)', 
      whatquiz: '월 16,667원부터',
      highlight: false
    },
    { 
      category: '서버 위치', 
      chatgpt: '해외 서버', 
      whatquiz: '공개 (파일 미국 아이오와 · DB 서울)',
      highlight: false
    },
  ];

  const switchReasons = [
    {
      title: '생기부 작성에 특화',
      desc: 'ChatGPT에서 생기부를 쓰려면 매번 복잡한 프롬프트를 작성해야 합니다. 왓퀴즈는 학생 정보만 입력하면 2026 기재요령에 맞는 생기부가 자동 생성됩니다.'
    },
    {
      title: '학교 예산으로 결제 가능',
      desc: 'ChatGPT Plus는 해외 결제만 지원해서 학교 예산 사용이 어렵습니다. 왓퀴즈는 국내 법인이라 법인카드, 공용카드 결제와 세금계산서 발급이 가능합니다.'
    },
    {
      title: '여러 AI 모델을 한 곳에서',
      desc: 'ChatGPT, Claude, Gemini를 각각 구독하면 월 8만원 이상입니다. 왓퀴즈 PRO 하나로 모든 최신 AI 모델을 사용할 수 있습니다.'
    },
    {
      title: '교사 업무에 최적화된 기능',
      desc: '생기부, 학습지, 수행평가, 채점까지 교사에게 필요한 기능이 모두 갖춰져 있습니다. 일반 AI에서는 얻을 수 없는 교육 현장 특화 기능입니다.'
    }
  ];

  const faqData = [
    {
      q: 'ChatGPT에서 왓퀴즈로 옮기면 기존 대화 내용은 어떻게 되나요?',
      a: '왓퀴즈는 별도의 서비스이므로 ChatGPT 대화 내용은 이전되지 않습니다. 다만, 왓퀴즈에서 생성한 생기부, 학습지는 모두 저장되어 언제든 다시 확인할 수 있습니다.'
    },
    {
      q: '왓퀴즈도 ChatGPT처럼 자유로운 대화가 가능한가요?',
      a: '네, 왓퀴즈의 "AI 교사 상담" 기능에서 ChatGPT와 동일하게 자유로운 대화가 가능합니다. 추가로 GPT, Claude, Gemini 중 원하는 모델을 선택할 수 있습니다.'
    },
    {
      q: '무료로 사용해볼 수 있나요?',
      a: '네, 가입만 하면 기본 기능을 무료로 쓸 수 있어요. 최신 모델, 파일 첨부, 엑셀 일괄 처리는 PRO 구독으로 이용하세요.'
    },
    {
      q: 'ChatGPT Plus보다 왓퀴즈 PRO가 더 저렴한가요?',
      a: 'ChatGPT Plus는 월 $20(약 27,000원)이고, 왓퀴즈 PRO 12개월 플랜은 월 16,667원입니다. 게다가 왓퀴즈는 GPT 외에 Claude, Gemini까지 모두 사용 가능합니다.'
    }
  ];

  return (
    <div className="compare-page">
      <Helmet>
        <title>ChatGPT vs 왓퀴즈 비교 | 교사용 AI 어떤 게 더 나을까?</title>
        <meta name="description" content="ChatGPT와 왓퀴즈 완벽 비교. 생기부 작성, 학습지 생성, 가격, 학교 예산 결제까지. 교사에게 더 맞는 AI는? 2026년 최신 비교." />
        <meta name="keywords" content="ChatGPT 대안, 왓퀴즈 vs ChatGPT, 교사용 AI, 생기부 AI, ChatGPT 교사, 학교 예산 AI" />
        <link rel="canonical" href="https://whatquiz.co.kr/compare/chatgpt" />
        <meta property="og:title" content="ChatGPT vs 왓퀴즈 | 교사를 위한 AI 비교" />
        <meta property="og:description" content="ChatGPT와 왓퀴즈, 교사에게 더 맞는 AI는? 생기부 작성, 가격, 기능 완벽 비교." />
        <meta property="og:url" content="https://whatquiz.co.kr/compare/chatgpt" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          })}
        </script>
      </Helmet>

      {/* 히어로 섹션 */}
      <section className="compare-hero">
        <div className="compare-hero-inner">
          <p className="compare-hero-label">2026년 최신 비교</p>
          <h1 className="compare-hero-title">
            ChatGPT vs 왓퀴즈<br />
            <span className="compare-hero-highlight">교사에게 더 맞는 AI는?</span>
          </h1>
          <p className="compare-hero-desc">
            일반 AI ChatGPT와 교사 전용 AI 왓퀴즈,<br />
            어떤 차이가 있는지 솔직하게 비교해드립니다.
          </p>
        </div>
      </section>

      {/* TL;DR 섹션 */}
      <section className="compare-tldr">
        <div className="compare-tldr-inner">
          <div style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'normal', border: 0 }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 700, color: '#3182f6' }}>핵심 답변</p>
            <h2 style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 800, color: '#111' }}>교사에게 ChatGPT와 왓퀴즈 중 무엇이 더 맞나요?</h2>
            <p style={{ margin: '0 0 16px', fontSize: '16px', lineHeight: 1.75, color: '#334155' }}>
              교육 외 일반적인 AI 작업에는 ChatGPT가 적합합니다. 하지만 생기부, 수행평가 채점, 학습지, 학교 예산 결제처럼 교사 업무 양식과 교육 현장 맥락이 중요한 경우에는 교사 전용 기능을 제공하는 왓퀴즈가 더 직접적인 선택입니다.
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
              비교 기준: 대상 사용자, 생기부 작성 흐름, 학교 예산 결제, 교육 규정 반영, AI 모델 접근성
            </p>
          </div>
          <h2 className="compare-section-title">한눈에 보는 결론</h2>
          <div className="compare-tldr-cards">
            <div className="compare-tldr-card">
              <h3>ChatGPT가 더 나은 경우</h3>
              <ul>
                <li>교육 외 다양한 분야에서 AI를 활용하고 싶을 때</li>
                <li>개인 비용으로 해외 결제가 가능할 때</li>
                <li>프롬프트 작성에 능숙하고 직접 튜닝을 원할 때</li>
              </ul>
            </div>
            <div className="compare-tldr-card compare-tldr-card--primary">
              <h3>왓퀴즈가 더 나은 경우</h3>
              <ul>
                <li>생기부, 학습지, 수행평가 등 교사 업무가 주 용도일 때</li>
                <li>학교 예산(법인카드)으로 결제해야 할 때</li>
                <li>2026 기재요령 등 교육 규정 준수가 중요할 때</li>
                <li>GPT, Claude, Gemini를 모두 사용하고 싶을 때</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 상세 비교표 */}
      <section className="compare-table-section">
        <div className="compare-table-inner">
          <h2 className="compare-section-title">상세 기능 비교</h2>
          <div className="compare-table-wrapper">
            <table className="compare-table">
              <thead>
                <tr>
                  <th>비교 항목</th>
                  <th>ChatGPT</th>
                  <th className="compare-table-highlight">왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className={row.highlight ? 'compare-table-row--highlight' : ''}>
                    <td className="compare-table-category">{row.category}</td>
                    <td>{row.chatgpt}</td>
                    <td className="compare-table-whatquiz">{row.whatquiz}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 전환 이유 섹션 */}
      <section className="compare-reasons">
        <div className="compare-reasons-inner">
          <h2 className="compare-section-title">교사들이 왓퀴즈로 전환하는 이유</h2>
          <div className="compare-reasons-grid">
            {switchReasons.map((reason, idx) => (
              <div key={idx} className="compare-reason-card">
                <h3>{reason.title}</h3>
                <p>{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ 섹션 */}
      <section className="compare-faq">
        <div className="compare-faq-inner">
          <h2 className="compare-section-title">자주 묻는 질문</h2>
          <div className="compare-faq-list">
            {faqData.map((faq, idx) => (
              <div key={idx} className="compare-faq-item">
                <h3 className="compare-faq-q">{faq.q}</h3>
                <p className="compare-faq-a">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="compare-cta">
        <div className="compare-cta-inner">
          <h2>교사를 위해 만들어진 AI,<br />직접 경험해보세요</h2>
          <p>가입만 하면 무료로 시작할 수 있습니다</p>
          <div className="compare-cta-buttons">
            <button 
              className="compare-cta-primary"
              onClick={() => navigate('/create-saenggibu')}
            >
              무료로 생기부 써보기
            </button>
            <button 
              className="compare-cta-secondary"
              onClick={() => navigate('/pricing')}
            >
              요금제 보기
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChatGPTCompare;
